/* ====================================================================
   VERDICT GOODS — vg-motion.js (ported from source motion.js)
   GSAP 3.13.0 + ScrollTrigger + SplitText + Lenis 1.3.11, vendored locally
   as vendor-*.js (no public CDN). Optional layer: never runs under
   prefers-reduced-motion, never in the Shopify theme editor, and the page
   is fully usable when it doesn't run (vg-site.js owns the fallbacks).
   ==================================================================== */
(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (window.Shopify && Shopify.designMode) return;
  if (reduce || !window.gsap || !window.ScrollTrigger) return;

  window.VG_MOTION = true;
  gsap.registerPlugin(ScrollTrigger);
  var hasSplit = !!window.SplitText;
  if (hasSplit) gsap.registerPlugin(SplitText);

  var EXPO = 'expo.out';          /* cubic-bezier(.19,1,.22,1) */
  var splits = [];

  /* ---------------------------------------------------------------
     Lenis — smooth scroll driven off GSAP's ticker so ScrollTrigger
     and the scroll position never disagree by a frame.
     --------------------------------------------------------------- */
  var lenis = null;
  function initLenis() {
    if (!window.Lenis) return;
    /* Embedded contexts (the Shopify admin's full-page preview, preview-bar
       iframes, any third-party embed) get NATIVE scroll: Lenis intercepts the
       wheel, and inside a host UI that interception can leave the wheel dead
       while the scrollbar still works. Everything else in the motion layer
       runs fine off native scroll. */
    try { if (window.self !== window.top) return; } catch (e) { return; }
    lenis = new Lenis({ duration: 1.05, smoothWheel: true, wheelMultiplier: 1 });
    window.VG_LENIS = lenis;   /* handle for debugging / QA scripting */
    /* Firefox does not re-fire its root ResizeObserver when the document
       regains its height after the intro lock, so the limit measured while
       locked (0) would survive forever and every wheel target clamps to 0:
       wheel dead until a window resize happens to re-measure. Re-measure at
       every point the document height legitimately changes, and self-heal on
       the first wheel tick if the limit is ever stale again. */
    lenis.on('virtual-scroll', function () {
      if (lenis.limit < 1 && document.documentElement.scrollHeight - window.innerHeight > 1) lenis.resize();
    });
    window.addEventListener('load', function () { lenis.resize(); });
    lenis.on('scroll', ScrollTrigger.update);
    /* when smoothing settles, force one final trigger pass — on heavy pages
       (4K + large imagery) a dropped tail frame could otherwise leave a
       scrubbed element stranded a few percent from its true position */
    window.addEventListener('scrollend', function () { ScrollTrigger.update(); }, true);
    gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    gsap.ticker.lagSmoothing(0);
    /* in-page anchors have to go through Lenis or they fight it */
    document.querySelectorAll('a[href^="#"]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        var id = a.getAttribute('href');
        if (id.length < 2) return;
        var t = document.querySelector(id);
        if (!t) return;
        e.preventDefault();
        lenis.scrollTo(t, { offset: -40 });
      });
    });
  }

  /* ---------------------------------------------------------------
     Split helpers. SplitText's mask option does the clip-wrapper for
     us; the manual path is only a safety net if the plugin is absent.
     --------------------------------------------------------------- */
  function splitLines(el) {
    if (hasSplit) {
      var s = SplitText.create(el, {
        type: 'lines', mask: 'lines',
        linesClass: 'vg-line', autoSplit: true,
        onSplit: function (self) { return self.lines; }
      });
      splits.push(s);
      [].forEach.call(el.querySelectorAll('.vg-line'), function (l) {
        if (l.parentElement) l.parentElement.classList.add('vg-line-mask');
      });
      return s.lines;
    }
    /* fallback: treat each block child as one line */
    var kids = [].slice.call(el.children);
    if (!kids.length) return [el];
    return kids.map(function (k) {
      var mask = document.createElement('span');
      mask.className = 'vg-line-mask';
      mask.style.display = 'block';
      el.insertBefore(mask, k);
      mask.appendChild(k);
      k.classList.add('vg-line');
      k.style.display = 'block';
      return k;
    });
  }

  function splitChars(el) {
    if (hasSplit) {
      var s = SplitText.create(el, {
        type: 'chars,words', mask: 'chars',
        charsClass: 'vg-char', autoSplit: true
      });
      splits.push(s);
      [].forEach.call(el.querySelectorAll('.vg-char'), function (c) {
        if (c.parentElement) c.parentElement.classList.add('vg-char-mask');
      });
      return s.chars;
    }
    return splitLines(el);
  }

  /* Build the reveal for one [data-split] element.
     data-split="chars" | "lines" (default) · data-split-start overrides trigger */
  function armSplit(el, opts) {
    opts = opts || {};
    var kind = el.getAttribute('data-split') || 'lines';
    var pieces = kind === 'chars' ? splitChars(el) : splitLines(el);
    el.classList.add('vg-armed');
    var isChars = kind === 'chars';
    var vars = {
      yPercent: 0, opacity: 1,
      duration: parseFloat(el.getAttribute('data-split-dur')) || (isChars ? 1.05 : 1.25),
      ease: EXPO,
      stagger: parseFloat(el.getAttribute('data-split-stagger')) || (isChars ? 0.016 : 0.09)
    };
    gsap.set(pieces, { yPercent: 118, opacity: isChars ? 0 : 1 });
    if (opts.timeline) { opts.timeline.to(pieces, vars, opts.position || 0); return pieces; }
    gsap.to(pieces, Object.assign(vars, {
      scrollTrigger: {
        trigger: opts.trigger || el,
        start: el.getAttribute('data-split-start') || 'top 84%',
        once: true
      }
    }));
    return pieces;
  }

  /* generic fade+rise for anything tagged [data-mo] */
  function armSimple() {
    document.querySelectorAll('[data-mo]').forEach(function (el) {
      el.classList.add('vg-armed');
      gsap.fromTo(el, { y: 34, opacity: 0 }, {
        y: 0, opacity: 1, duration: 1, ease: EXPO,
        delay: parseFloat(el.getAttribute('data-mo')) || 0,
        scrollTrigger: { trigger: el, start: 'top 88%', once: true }
      });
    });
  }

  /* ---------------------------------------------------------------
     1 · INTRO — the cookie loader hands off to a two-panel wipe, and
     the hero timeline starts the instant the panels clear.
     --------------------------------------------------------------- */
  function initIntro(done) {
    var loader = document.getElementById('loader');
    var wipe = document.querySelector('.vg-wipe');
    var count = document.querySelector('.vg-count');
    if (!loader || !wipe) { done(); return; }

    document.documentElement.classList.add('vg-locked');
    if (lenis) lenis.stop();

    var panels = wipe.querySelectorAll('span');
    gsap.set(panels, { yPercent: 0 });
    gsap.set(wipe, { autoAlpha: 0 });

    var counter = { v: 0 };
    var finished = false;
    function finish() {
      if (finished) return;
      finished = true;
      document.documentElement.classList.remove('vg-locked');
      /* the document just regained its full height; re-measure so the
         scroll limit is real (Firefox never re-measures on its own here) */
      if (lenis) { lenis.resize(); lenis.start(); }
      wipe.remove();
      if (count) count.remove();
      loader.remove();
      /* the bite-notch clip-paths are measured off live box sizes; the intro
         and the webfont both land after the first pass, so re-fit them here */
      if (window.Verdict && window.Verdict.fitBite) {
        document.querySelectorAll('.drop').forEach(window.Verdict.fitBite);
      }
      ScrollTrigger.refresh();
      done();
    }
    var tl = gsap.timeline({ onComplete: finish });
    /* requestAnimationFrame is suspended in a background tab, which would
       otherwise strand the visitor behind a locked intro. setTimeout still
       fires there, so give the whole sequence a hard ceiling. */
    setTimeout(function () { if (!finished) { tl.progress(1); finish(); } }, 6000);

    /* the cookie-eaten loader keeps its 1.5s beat, we just time to it */
    tl.to(counter, {
      v: 100, duration: 1.5, ease: 'power1.inOut',
      onUpdate: function () { if (count) count.textContent = String(Math.round(counter.v)).padStart(3, '0'); }
    })
      .set(wipe, { autoAlpha: 1 })
      .to(loader, { autoAlpha: 0, duration: 0.35, ease: 'power2.inOut' }, '-=0.05')
      .to(count, { autoAlpha: 0, duration: 0.4, ease: 'power2.in' }, '<')
      .to(panels[0], { yPercent: -100, duration: 1.05, ease: EXPO }, '-=0.15')
      .to(panels[1], { yPercent: 100, duration: 1.05, ease: EXPO }, '<0.06');
  }

  /* ---------------------------------------------------------------
     2 · HERO — media settles out of scale(1.25) while the headline
     rises character by character, then the whole thing lifts away on
     scroll with the photo drifting slower than the type.
     --------------------------------------------------------------- */
  function initHero() {
    var hero = document.querySelector('[data-hero]');
    if (!hero) return;
    var img = hero.querySelector('.hero-bg img');
    var h1 = hero.querySelector('h1');
    var eyebrow = hero.querySelector('.eyebrow');
    var inner = hero.querySelector('.hero-inner');
    /* the supporting copy/CTA under the headline, whatever each page calls it */
    var rest = [].slice.call(hero.querySelectorAll(
      '.hero-tease, .sub, .hero-cta, .count, .next, .c-grid, .stamp-big, .meta-line, .sum, .wl-timer-block'
    ));
    var hint = hero.querySelector('.hero-scroll');

    var tl = gsap.timeline({ defaults: { ease: EXPO } });
    if (img) tl.fromTo(img, { scale: 1.25 }, { scale: 1, duration: 2.2 }, 0);
    if (eyebrow) tl.fromTo(eyebrow, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 1 }, 0.15);
    /* an empty headline is one case.js fills in later — initLateSplit owns it */
    if (h1 && h1.textContent.trim() && !h1.hasAttribute('data-split-late')) {
      armSplit(h1, { timeline: tl, position: 0.25 });
    }
    if (rest.length) tl.fromTo(rest, { y: 24, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, stagger: 0.08 }, 0.6);
    if (hint) tl.fromTo(hint, { opacity: 0 }, { opacity: 1, duration: 0.8 }, 0.95);

    /* scroll-out: type leaves faster than the photo — cheap depth */
    if (inner) gsap.to(inner, {
      y: -90, opacity: 0.15, ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
    });
    if (img) gsap.to(img, {
      scale: 1.14, yPercent: 8, ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
    });
    if (hint) gsap.to(hint, {
      opacity: 0, ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: '25% top', scrub: true }
    });
  }

  /* ---------------------------------------------------------------
     3 · FILE TABS — slide in from their own edge, then keep drifting
     a little against the scroll so the page never sits still.
     --------------------------------------------------------------- */
  function initFileTabs() {
    document.querySelectorAll('.file-tab').forEach(function (tab) {
      var right = tab.getAttribute('data-pos') === 'r';
      gsap.fromTo(tab, { x: right ? 140 : -140, opacity: 0 }, {
        x: 0, opacity: 1, duration: 1.3, ease: EXPO,
        scrollTrigger: { trigger: tab, start: 'top 95%', once: true }
      });
    });
  }

  /* ---------------------------------------------------------------
     4 · THE FOUNDER — three prints fly in from big rotations and
     settle onto their resting tilt, then drift at different rates.
     GSAP only touches --vgY/--vgR/--vgS so the CSS keeps owning the
     static tilt and the hover state.
     --------------------------------------------------------------- */
  function initPrints() {
    var prints = document.querySelectorAll('.sec-selection .print');
    if (!prints.length) return;
    var entry = [
      { r: -16, y: 130 },
      { r: 14, y: 170 },
      { r: -10, y: 210 }
    ];
    prints.forEach(function (p, i) {
      var e = entry[i % entry.length];
      gsap.fromTo(p,
        { '--vgR': e.r + 'deg', '--vgY': e.y + 'px', '--vgS': 0.9, opacity: 0 },
        {
          '--vgR': '0deg', '--vgY': '0px', '--vgS': 1, opacity: 1,
          duration: 2, ease: EXPO, delay: i * 0.11,
          scrollTrigger: { trigger: '.sec-selection .prints', start: 'top 88%', once: true }
        });
      /* after they land, each one drifts on its own scroll rate */
      gsap.to(p, {
        '--vgY': (i === 1 ? -46 : -18) + 'px', ease: 'none',
        scrollTrigger: { trigger: '.sec-selection', start: 'top 40%', end: 'bottom top', scrub: 1 }
      });
    });
  }

  /* Any other .fly on the site. motion.css holds every .fly at opacity 0
     for the reveal, but initPrints only claims the homepage founder prints
     — without this, a .fly anywhere else (the founder portrait) would sit
     invisible forever. */
  function initFlyRest() {
    var rest = [].slice.call(document.querySelectorAll('.fly'))
      .filter(function (el) { return !el.closest('.sec-selection .prints'); });
    rest.forEach(function (el, i) {
      gsap.fromTo(el,
        { opacity: 0, y: 90, rotate: i % 2 ? 5 : -5, scale: 0.94 },
        {
          opacity: 1, y: 0, rotate: 0, scale: 1, duration: 1.6, ease: EXPO,
          scrollTrigger: { trigger: el, start: 'top 88%', once: true }
        });
    });
  }

  /* ---------------------------------------------------------------
     5 · STAGGER GROUPS — product cards, criteria grids, jury tiles.
     case.js injects several of these, so wait for the fill.
     --------------------------------------------------------------- */
  function initStaggers() {
    document.querySelectorAll('[data-stagger]').forEach(function (wrap) {
      function arm() {
        var kids = wrap.children;
        if (!kids.length) return false;
        gsap.fromTo(kids,
          { y: 70, rotate: 2.5, opacity: 0 },
          {
            y: 0, rotate: 0, opacity: 1, duration: 1.4, ease: EXPO, stagger: 0.12,
            scrollTrigger: { trigger: wrap, start: 'top 85%', once: true }
          });
        ScrollTrigger.refresh();
        return true;
      }
      if (arm()) return;
      var mo = new MutationObserver(function () { if (arm()) mo.disconnect(); });
      mo.observe(wrap, { childList: true });
    });
  }

  /* Headlines that case.js writes in after load (the PDP title) can only be
     split once they actually hold text. */
  function initLateSplit() {
    document.querySelectorAll('[data-split-late]').forEach(function (el) {
      function arm() {
        if (!el.textContent.trim()) return false;
        el.setAttribute('data-split', el.getAttribute('data-split-late'));
        armSplit(el);
        ScrollTrigger.refresh();
        return true;
      }
      if (arm()) return;
      var mo = new MutationObserver(function () { if (arm()) mo.disconnect(); });
      mo.observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ---------------------------------------------------------------
     6 · THE DROP BAND — the centrepiece. The section pins for one
     screen and the three lines land one at a time as you scroll,
     while the product shot unrotates behind them.
     --------------------------------------------------------------- */
  function initDropBand() {
    var band = document.querySelector('.sec-drop-band');
    if (!band) return;
    var h2 = band.querySelector('h2');
    var bg = band.querySelector('.band-bg');
    var eyebrow = band.querySelector('.eyebrow');
    var philo = band.querySelector('.band-philo');
    var cta = band.querySelector('.band-cta');

    var lines = h2 ? splitLines(h2) : [];
    if (h2) h2.classList.add('vg-armed');
    gsap.set(lines, { yPercent: 118 });
    gsap.set([eyebrow, philo, cta].filter(Boolean), { opacity: 0, y: 30 });
    if (bg) gsap.set(bg, { scale: 1.18, rotate: -4, opacity: 0.6 });

    /* Plays ONCE on entry (was scrubbed: on tall/4K viewports the scrub window
       resolved so late the band sat half-armed through most of its pass, and a
       dropped frame could strand it mid-state). A timed entrance lands the
       same choreography promptly and always completes. */
    var tl = gsap.timeline({
      defaults: { ease: EXPO },
      scrollTrigger: { trigger: band, start: 'top 75%', once: true }
    });
    if (bg) tl.to(bg, { scale: 1, rotate: 0, opacity: 1, duration: 1.6 }, 0);
    tl.to(eyebrow, { opacity: 1, y: 0, duration: 0.6 }, 0.05);
    lines.forEach(function (l, i) { tl.to(l, { yPercent: 0, duration: 0.9 }, 0.15 + i * 0.14); });
    tl.to(philo, { opacity: 1, y: 0, duration: 0.8 }, 0.6)
      .to(cta, { opacity: 1, y: 0, duration: 0.8 }, 0.72);
  }

  /* ---------------------------------------------------------------
     7 · EVIDENCE TICKER — 60s linear loop, but its speed and
     direction answer to the scroll, which is what makes a marquee
     feel authored instead of decorative.
     --------------------------------------------------------------- */
  function initTicker() {
    var track = document.querySelector('.vg-ticker-track');
    if (!track) return;
    /* duplicate the set until it comfortably covers two screens — capped,
       because a zero-width track (hidden, or webfont not in yet) would
       otherwise spin this loop forever */
    var seed = track.innerHTML;
    for (var i = 0; i < 12 && track.scrollWidth < window.innerWidth * 2; i++) track.innerHTML += seed;
    var half = track.scrollWidth / 2;
    if (!half) return;

    var loop = gsap.to(track, {
      x: -half, duration: 26, ease: 'none', repeat: -1,
      modifiers: { x: function (x) { return (parseFloat(x) % half) + 'px'; } }
    });

    var dir = 1;
    ScrollTrigger.create({
      trigger: document.body, start: 'top top', end: 'bottom bottom',
      onUpdate: function (self) {
        var d = self.direction;
        if (d !== dir) { dir = d; gsap.to(loop, { timeScale: d, duration: 0.4, overwrite: true }); }
        var boost = 1 + Math.min(6, Math.abs(self.getVelocity()) / 320);
        gsap.to(loop, { timeScale: dir * boost, duration: 0.25, overwrite: 'auto' });
        gsap.to(track, { skewX: gsap.utils.clamp(-8, 8, self.getVelocity() / -260), duration: 0.4, overwrite: 'auto' });
      }
    });
  }

  /* ---------------------------------------------------------------
     8 · SOURCES — the red strings draw themselves as you scroll
     through the wall. site.js rebuilds the paths on resize, so
     re-arm whenever it does.
     --------------------------------------------------------------- */
  function initStrings() {
    var wall = document.querySelector('[data-board]');
    var svg = wall && wall.querySelector('.bd-strings');
    if (!svg || !wall) return;
    var st = null;
    function arm() {
      var paths = svg.querySelectorAll('path');
      if (!paths.length) return;
      gsap.set(paths, { strokeDasharray: 1, strokeDashoffset: 1 });
      if (st) st.kill();
      st = gsap.to(paths, {
        strokeDashoffset: 0, ease: 'none', stagger: 0.25,
        scrollTrigger: { trigger: wall, start: 'top 72%', end: 'center 45%', scrub: 0.8 }
      }).scrollTrigger;
    }
    arm();
    new MutationObserver(arm).observe(svg, { childList: true });
    /* site.js owns the path geometry. Have it re-measure after our layout
       passes, otherwise the paths keep whatever positions the cards had
       mid-entrance and the strings float off their pins. */
    var redraw = window.VerdictSite && window.VerdictSite.redrawBoardStrings;
    if (redraw) { ScrollTrigger.addEventListener('refresh', redraw); redraw(); }
  }

  /* ---------------------------------------------------------------
     9 · CLOSE — the monogram badge keeps turning as the section
     passes, and the headline lands character by character.
     --------------------------------------------------------------- */
  function initClose() {
    var badge = document.querySelector('.sec-close .vg-close-badge');
    if (!badge) return;
    gsap.fromTo(badge, { rotate: -34, scale: 0.86 }, {
      rotate: 0, scale: 1, ease: 'none',
      scrollTrigger: { trigger: '.sec-close', start: 'top 85%', end: 'center center', scrub: 0.7 }
    });
  }

  /* ---------------------------------------------------------------
     10 · MAGNETIC BUTTONS — the cookie-bite CTAs lean toward the
     cursor. Pointer-fine only; never on touch.
     --------------------------------------------------------------- */
  function initMagnets() {
    if (!window.matchMedia('(hover:hover) and (pointer:fine)').matches) return;
    document.querySelectorAll('.drop').forEach(function (btn) {
      var qx = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'power3.out' });
      var qy = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'power3.out' });
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        qx((e.clientX - (r.left + r.width / 2)) * 0.22);
        qy((e.clientY - (r.top + r.height / 2)) * 0.32);
      });
      btn.addEventListener('pointerleave', function () { qx(0); qy(0); });
    });
  }

  /* --------------------------------------------------------------- */
  function armSections() {
    document.querySelectorAll('[data-split]').forEach(function (el) {
      if (!el.classList.contains('vg-armed')) armSplit(el);
    });
    armSimple();
    initLateSplit();
    initFileTabs();
    initPrints();
    initFlyRest();
    initStaggers();
    initDropBand();
    initTicker();
    initStrings();
    initClose();
    initMagnets();
    ScrollTrigger.refresh();
  }

  function boot() {
    initLenis();
    /* hold every scroll trigger until the intro has cleared, otherwise
       reveals burn off behind the wipe */
    initIntro(function () {
      initHero();
      armSections();
    });
    /* absolute failsafe: whatever happens to the intro timeline, the page
       must never stay wheel-locked. (The intro carries its own 6s ceiling;
       this is the belt to that suspender.) */
    setTimeout(function () {
      if (document.documentElement.classList.contains('vg-locked')) {
        document.documentElement.classList.remove('vg-locked');
        if (lenis) lenis.resize();
      }
      if (lenis && lenis.isStopped) lenis.start();
    }, 7000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* fonts change line boxes — resplit once Oswald is actually in */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
  }
  window.addEventListener('load', function () { ScrollTrigger.refresh(); });
})();
